import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PageWrapper from "./layout/PageWrapper";
import PageHeader from "./layout/PageHeader";
import StatsCard from "./layout/StatsCard";
import FormStatsDateFilter from "./FormStatsDateFilter";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TodayIcon from "@mui/icons-material/Today";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import EuroIcon from "@mui/icons-material/Euro";

const AdminSalesStats = ({
  stats,
  periodType,
  startDate,
  endDate,
  onPeriodTypeChange,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  onRefreshData,
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const getTrendText = (trend) => {
    if (trend > 0) return `+${trend.toFixed(1)}%`;
    if (trend < 0) return `${trend.toFixed(1)}%`;
    return "0%";
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return "success";
    if (trend < 0) return "error";
    return "default";
  };

  return (
    <PageWrapper>
      <PageHeader
        title="Statistiques des Ventes"
        subtitle="Analysez l'évolution des ventes et revenus"
      />

      {/* Filtres de période */}
      <FormStatsDateFilter
        periodType={periodType}
        startDate={startDate}
        endDate={endDate}
        onPeriodTypeChange={onPeriodTypeChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onApplyFilters={onApplyFilters}
        onRefresh={onRefreshData}
      />

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Ventes"
            value={stats.totalSales}
            icon={ShoppingCartIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Revenu Total"
            value={formatCurrency(stats.totalRevenue)}
            icon={EuroIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Tendance"
            value={getTrendText(stats.trend)}
            icon={TrendingUpIcon}
            color={getTrendColor(stats.trend)}
            trend={
              stats.trend > 0 ? "up" : stats.trend < 0 ? "down" : "neutral"
            }
            trendValue={getTrendText(stats.trend)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Moyenne / Jour"
            value={stats.averagePerDay.toFixed(1)}
            icon={TodayIcon}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Graphiques et tableaux */}
      <Grid container spacing={3}>
        {/* Graphique par date */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <BarChartIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6" component="h2">
                  Ventes par Date
                </Typography>
              </Box>

              {stats.byDate.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Ventes</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Revenu</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ width: "200px" }}>
                          <strong>Graphique</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.byDate.map((item, index) => {
                        const maxCount = Math.max(
                          ...stats.byDate.map((d) => d.count)
                        );
                        const barWidth = (item.count / maxCount) * 100;

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <TableCell>{formatDate(item.date)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "medium" }}
                              >
                                {item.count}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "medium",
                                  color: "success.main",
                                }}
                              >
                                {formatCurrency(item.revenue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${barWidth}%`,
                                    height: 8,
                                    bgcolor: "primary.main",
                                    borderRadius: 1,
                                    minWidth: barWidth > 0 ? "4px" : "0px",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary", minWidth: 30 }}
                                >
                                  {item.count}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" align="center">
                  Aucune donnée disponible pour cette période
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top utilisateurs */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <PeopleIcon sx={{ mr: 1, color: "secondary.main" }} />
                <Typography variant="h6" component="h2">
                  Top Utilisateurs
                </Typography>
              </Box>

              {stats.byUser.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Utilisateur</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Ventes</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Revenu</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.byUser.map((user, index) => (
                        <TableRow
                          key={user.userId}
                          sx={{
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {user.userName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "medium",
                                color:
                                  index === 0 ? "primary.main" : "text.primary",
                              }}
                            >
                              {user.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: "medium",
                                color: "success.main",
                              }}
                            >
                              {formatCurrency(user.revenue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" align="center">
                  Aucun utilisateur trouvé
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default AdminSalesStats;
